import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, SettingsRow, SettingsSection, Switch } from '@wundo/ui';
import { SaveStatus } from '@/components/SaveStatus.tsx';
import { AddColleagueDialog } from '@/components/meetings/AddColleagueDialog.tsx';
import { AddExceptionDialog } from '@/components/meetings/AddExceptionDialog.tsx';
import {
  fetchFollowExceptions,
  fetchFollowPreferences,
  removeFollowException,
  saveFollowPreferences,
  type MeetingFollowExceptionItem,
  type MeetingFollowPreferences,
} from '@/lib/meetingFollow.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/meetings')({
  component: MeetingSettingsPage,
});

export function MeetingSettingsPage() {
  const queryClient = useQueryClient();

  const {
    data: preferences,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetingFollowPreferences'],
    queryFn: fetchFollowPreferences,
  });

  const { data: exceptions } = useQuery({
    queryKey: ['meetingFollowExceptions'],
    queryFn: fetchFollowExceptions,
  });

  const save = useMutation({
    mutationFn: saveFollowPreferences,
    /* The server is what decides what was stored, so the page follows it. */
    onSuccess: (saved) => queryClient.setQueryData(['meetingFollowPreferences'], saved),
  });

  const remove = useMutation({
    mutationFn: removeFollowException,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['meetingFollowExceptions'] }),
  });

  if (isPending) return null;
  if (isError) {
    return <p className='text-base text-ink-muted'>{m.meetings_load_failed()}</p>;
  }

  /* Save the whole choice every time: one PUT, one stored record, no merging. */
  const change = (patch: Partial<MeetingFollowPreferences>) =>
    save.mutate({
      follow_one_on_ones: preferences.follow_one_on_ones,
      follow_team_meetings: preferences.follow_team_meetings,
      follow_all: preferences.follow_all,
      notetaker: preferences.notetaker,
      ...patch,
    });

  /*
   * "All conversations" covers both kinds, so it turns them on and locks them
   * rather than sitting beside them: two switches that change nothing while it
   * is on would be a choice that is not one. Turning it off turns everything
   * off — it is one setting, not a layer over the other two.
   */
  const changeFollowAll = (checked: boolean) =>
    change({
      follow_all: checked,
      follow_one_on_ones: checked,
      follow_team_meetings: checked,
    });

  const kindsLocked = preferences.follow_all;

  const followed = (exceptions ?? []).filter(isFollowedColleague);
  const switchedOff = (exceptions ?? []).filter(
    (exception) => !isFollowedColleague(exception),
  );

  /* The kind already takes every one-to-one, so picking people adds nothing. */
  const oneOnOnesCovered = preferences.follow_one_on_ones;

  return (
    <>
      <SaveStatus pending={save.isPending} failed={save.isError} />

      <SettingsSection
        title={m.meetings_kinds_title()}
        description={m.meetings_kinds_desc()}
      >
        <SettingsRow
          label={m.meetings_kind_one_on_ones()}
          description={
            kindsLocked
              ? m.meetings_kind_covered_by_all()
              : m.meetings_kind_one_on_ones_desc()
          }
        >
          <Switch
            aria-label={m.meetings_kind_one_on_ones()}
            disabled={kindsLocked}
            checked={preferences.follow_one_on_ones}
            onCheckedChange={(checked) => change({ follow_one_on_ones: checked })}
          />
        </SettingsRow>
        <SettingsRow
          label={m.meetings_kind_team()}
          description={
            kindsLocked ? m.meetings_kind_covered_by_all() : m.meetings_kind_team_desc()
          }
        >
          <Switch
            aria-label={m.meetings_kind_team()}
            disabled={kindsLocked}
            checked={preferences.follow_team_meetings}
            onCheckedChange={(checked) => change({ follow_team_meetings: checked })}
          />
        </SettingsRow>
        <SettingsRow
          label={m.meetings_kind_all()}
          description={m.meetings_kind_all_desc()}
        >
          <Switch
            aria-label={m.meetings_kind_all()}
            checked={preferences.follow_all}
            onCheckedChange={changeFollowAll}
          />
        </SettingsRow>
      </SettingsSection>

      {/*
       * Right under the kinds, because picking colleagues is the same choice
       * made person by person. The kind that covers them all hides the list
       * the way "alle gesprekken" hides the kinds it covers; the picks stay.
       */}
      <SettingsSection
        title={m.meetings_colleagues_title()}
        description={oneOnOnesCovered ? undefined : m.meetings_colleagues_desc()}
        action={
          oneOnOnesCovered ? undefined : (
            <AddColleagueDialog corrections={exceptions ?? []} />
          )
        }
      >
        {oneOnOnesCovered ? (
          <p className='px-5.5 py-4 text-sm text-ink-muted'>
            {m.meetings_colleagues_covered()}
          </p>
        ) : followed.length > 0 ? (
          followed.map((colleague) => (
            <SettingsRow
              key={colleague.id}
              label={colleague.label ?? colleague.person_email}
              description={m.meetings_colleague_one_on_ones()}
            >
              <Button
                size='sm'
                variant='dangerQuiet'
                disabled={remove.isPending}
                onClick={() => remove.mutate(colleague.id)}
              >
                {m.meetings_exception_remove()}
              </Button>
            </SettingsRow>
          ))
        ) : (
          <p className='px-5.5 py-4 text-sm text-ink-muted'>
            {m.meetings_colleagues_empty()}
          </p>
        )}
      </SettingsSection>

      <SettingsSection title={m.meetings_notetaker_title()}>
        <SettingsRow
          label={m.meetings_notetaker()}
          description={m.meetings_notetaker_desc()}
        >
          <Switch
            aria-label={m.meetings_notetaker()}
            checked={preferences.notetaker}
            onCheckedChange={(checked) => change({ notetaker: checked })}
          />
        </SettingsRow>
      </SettingsSection>

      <SettingsSection
        title={m.meetings_exceptions_title()}
        description={m.meetings_exceptions_desc()}
        action={<AddExceptionDialog />}
      >
        {switchedOff.length > 0 ? (
          switchedOff.map((exception) => (
            <SettingsRow
              key={exception.id}
              label={exception.label ?? m.meetings_exception_unknown()}
              description={ruleOf(exception)}
            >
              <Button
                size='sm'
                variant='dangerQuiet'
                disabled={remove.isPending}
                onClick={() => remove.mutate(exception.id)}
              >
                {m.meetings_exception_remove()}
              </Button>
            </SettingsRow>
          ))
        ) : (
          <p className='px-5.5 py-4 text-sm text-ink-muted'>
            {m.meetings_exceptions_empty()}
          </p>
        )}
      </SettingsSection>
    </>
  );
}

/* A colleague whose one-to-ones Wundo follows: a pick, not an exception. */
function isFollowedColleague(exception: MeetingFollowExceptionItem): boolean {
  return exception.target_kind === 'person' && exception.follow === true;
}

/*
 * What was switched off, in the user's words. `follow` outranks `record`: a
 * meeting Wundo does not follow has no report to speak of either.
 */
function ruleOf(exception: MeetingFollowExceptionItem): string {
  if (exception.follow === false) return m.meetings_exception_no_follow();
  return m.meetings_exception_no_record();
}
